import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, PackageSearch, Search, ShoppingBag, X } from "lucide-react";
import "./header.css";
import { useCart } from "@/context/CartContext";
import { categories as mockCategories } from "@/mocks/products";
import { fetchStoreCategories, isStoreEnabled, type StoreCategory } from "@/services/store-api";
import { store } from "@/services/store";
import { useScrollLock } from "@/utils/useScrollLock";

const categoryHref = (id: string) => `/produtos?categoria=${encodeURIComponent(id)}`;

// Categorias reais da loja (Yampi). A lista vem do cache do servidor e é
// buscada uma vez por visita; se falhar, o menu mostra só "Ver tudo".
function useStoreCategories(): StoreCategory[] {
  const [list, setList] = useState<StoreCategory[]>(
    isStoreEnabled() ? [] : mockCategories.map((c) => ({ id: c.id, label: c.label }))
  );
  useEffect(() => {
    if (!isStoreEnabled()) return;
    let active = true;
    fetchStoreCategories()
      .then((categories) => { if (active) setList(categories); })
      .catch(() => { /* sem categorias: só "Ver tudo" */ });
    return () => { active = false; };
  }, []);
  return list;
}

export default function Header({ variant = "solid" }: { variant?: "overlay" | "solid" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const overlay = variant === "overlay";
  const { totalItems, openCart } = useCart();
  const storeCategories = useStoreCategories();
  // Na página inicial o cabeçalho fica sobre a foto; ao rolar vira a barra branca.
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  useScrollLock(menuOpen);

  useEffect(() => {
    if (!overlay) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [overlay]);

  useEffect(() => {
    setMegaOpen(false);
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const term = query.trim();
    if (term) navigate(`/produtos?q=${encodeURIComponent(term)}`);
  };

  const transparent = overlay && !scrolled && !megaOpen && !searchOpen;

  return (
    <header className={`bz-header ${overlay ? "is-overlay" : ""} ${transparent ? "is-transparent" : ""}`} onMouseLeave={() => setMegaOpen(false)}>
      <p className="bz-announce">Enviamos para todo o Brasil</p>

      <div className="bz-bar">
        <button type="button" className="bz-icon-btn bz-menu-toggle" aria-label="Abrir menu" aria-expanded={menuOpen} aria-controls="bz-drawer" onClick={() => setMenuOpen(true)}>
          <Menu size={24} strokeWidth={1.5} aria-hidden="true" />
        </button>

        <Link to="/" className="bz-logo" aria-label="Bozzetti Store, página inicial">
          <img src={`${import.meta.env.BASE_URL}bozzetti-logo.webp`} alt="Bozzetti Store" />
        </Link>

        <nav className="bz-nav" aria-label="Principal">
          <div className="bz-nav-item"><Link to="/produtos?colecao=novidades" onMouseEnter={() => setMegaOpen(false)}>Novidades</Link></div>
          <div className="bz-nav-item"><Link to="/produtos" aria-expanded={megaOpen} onMouseEnter={() => setMegaOpen(true)} onFocus={() => setMegaOpen(true)}>Roupas</Link></div>
          <div className="bz-nav-item"><Link to="/produtos?colecao=vendidos" onMouseEnter={() => setMegaOpen(false)}>Mais vendidos</Link></div>
          <div className="bz-nav-item"><Link to="/produtos?colecao=promocoes" onMouseEnter={() => setMegaOpen(false)}>Promoções</Link></div>
          <div className="bz-nav-item"><Link to="/sobre" onMouseEnter={() => setMegaOpen(false)}>Sobre</Link></div>
        </nav>

        <div className="bz-actions">
          <form className="bz-search-form" role="search" onSubmit={submitSearch}>
            <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar..." aria-label="Buscar produtos" />
            <button type="submit" className="bz-icon-btn" aria-label="Buscar"><Search size={23} strokeWidth={1.5} aria-hidden="true" /></button>
          </form>
          <button type="button" className="bz-icon-btn bz-search-toggle" aria-label="Buscar" aria-expanded={searchOpen} onClick={() => setSearchOpen((open) => !open)}>
            <Search size={22} strokeWidth={1.5} aria-hidden="true" />
          </button>
          <Link to="/meus-pedidos" className="bz-icon-btn bz-track" aria-label="Rastrear pedido">
            <PackageSearch size={25} strokeWidth={1.4} aria-hidden="true" />
          </Link>
          <button type="button" className="bz-icon-btn bz-bag" aria-label={`Abrir sacola, ${totalItems} itens`} onClick={openCart}>
            <ShoppingBag size={25} strokeWidth={1.4} aria-hidden="true" />
            {totalItems > 0 && <span>{totalItems}</span>}
          </button>
        </div>
      </div>

      {searchOpen && (
        <form className="bz-mobile-search" role="search" onSubmit={submitSearch}>
          <input autoFocus type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar..." aria-label="Buscar produtos" />
        </form>
      )}

      {megaOpen && (
        <div className="bz-mega">
          <div>
            <h2>Roupas</h2>
            <ul>
              {storeCategories.map((category) => <li key={category.id}><Link to={categoryHref(category.id)}>{category.label}</Link></li>)}
              <li><Link to="/produtos">Ver tudo</Link></li>
            </ul>
          </div>
          <div>
            <h2>Coleções</h2>
            <ul>
              <li><Link to="/produtos?colecao=novidades">Novidades</Link></li>
              <li><Link to="/produtos?colecao=vendidos">Mais vendidos</Link></li>
              <li><Link to="/produtos?colecao=promocoes">Promoções</Link></li>
            </ul>
          </div>
          <div>
            <h2>Atendimento</h2>
            <ul>
              <li><Link to="/sobre">Sobre a Bozzetti</Link></li>
              <li><Link to="/meus-pedidos">Meus pedidos</Link></li>
              <li><Link to="/politica-de-trocas">Trocas e devoluções</Link></li>
              <li><Link to="/contato">Fale conosco</Link></li>
              <li><Link to="/afiliados">Programa de afiliados</Link></li>
            </ul>
          </div>
        </div>
      )}

      {menuOpen && (
        <>
          <div className="bz-drawer-scrim" onClick={() => setMenuOpen(false)}></div>
          <div id="bz-drawer" className="bz-drawer" role="dialog" aria-modal="true" aria-label="Menu">
            <div className="bz-drawer-head">
              Menu
              <button type="button" className="bz-icon-btn" aria-label="Fechar menu" onClick={() => setMenuOpen(false)}>
                <X size={22} strokeWidth={1.5} aria-hidden="true" />
              </button>
            </div>
            <Link to="/produtos?colecao=novidades">Novidades</Link>
            {storeCategories.map((category) => <Link key={category.id} to={categoryHref(category.id)}>{category.label}</Link>)}
            <Link to="/produtos">Ver tudo</Link>
            <Link to="/produtos?colecao=vendidos">Mais vendidos</Link>
            <Link to="/produtos?colecao=promocoes">Promoções</Link>
            <Link to="/sobre">Sobre a Bozzetti</Link>
            <Link to="/meus-pedidos">Meus pedidos</Link>
            <Link to="/contato">Fale conosco</Link>
            <Link to="/afiliados">Programa de afiliados</Link>
            <a href={store.whatsapp} target="_blank" rel="noopener noreferrer">WhatsApp {store.phone}</a>
          </div>
        </>
      )}
    </header>
  );
}
